import React from 'react';

import {
  ActivatedIcon,
  CanceledStatusIcon,
  DeactivatedIcon,
  DeploymentIcon,
  ServingActiveIcon,
  ServingStoppedIcon,
  TimeIcon,
} from '@/shared/assets/icons';

import styles from './RunStatusTag.module.css';

export type RunStatus =
  | 'execution_ready'
  | 'execution_running'
  | 'execution_succeeded'
  | 'execution_failed'
  | 'execution_canceled'
  | 'serving_inactive'
  | 'serving_active'
  | 'deployment_in_progress'
  | 'deployment_succeeded'
  | 'deployment_failed'
  | 'serving_in_progress'
  | 'serving_failed';

export type RunStatusInput = RunStatus | 'execution_queued';

function resolveRunStatusForDisplay(status: RunStatusInput): RunStatus {
  return status === 'execution_queued' ? 'execution_running' : status;
}

type RunStatusTagProps = {
  status: RunStatusInput;
};

const STATUS_CONFIG: Record<
  RunStatus,
  {
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    label: string;
    colorClass: string;
  }
> = {
  execution_ready: {
    icon: TimeIcon,
    label: 'Execution Ready',
    colorClass: styles.gray,
  },
  execution_running: {
    icon: TimeIcon,
    label: 'Execution Running',
    colorClass: styles.purple,
  },
  execution_succeeded: {
    icon: ActivatedIcon,
    label: 'Execution Succeeded',
    colorClass: styles.emerald,
  },
  execution_failed: {
    icon: DeactivatedIcon,
    label: 'Execution Failed',
    colorClass: styles.red,
  },
  execution_canceled: {
    icon: CanceledStatusIcon,
    label: 'Execution Canceled',
    colorClass: styles.orange,
  },
  serving_inactive: {
    icon: ServingStoppedIcon,
    label: 'Serving Inactive',
    colorClass: styles.gray,
  },
  serving_active: {
    icon: ServingActiveIcon,
    label: 'Serving Active',
    colorClass: styles.purple,
  },
  deployment_in_progress: {
    icon: TimeIcon,
    label: 'Deploying',
    colorClass: styles.purple,
  },
  deployment_succeeded: {
    icon: DeploymentIcon,
    label: 'Deployment Succeeded',
    colorClass: styles.emerald,
  },
  deployment_failed: {
    icon: DeploymentIcon,
    label: 'Deployment Failed',
    colorClass: styles.red,
  },
  serving_in_progress: {
    icon: TimeIcon,
    label: 'Deploying',
    colorClass: styles.purple,
  },
  serving_failed: {
    icon: DeploymentIcon,
    label: 'Deployment Failed',
    colorClass: styles.red,
  },
};

export const RUN_STATUS_VALUES = Object.keys(STATUS_CONFIG) as RunStatus[];

export function isValidRunStatus(status: unknown): status is RunStatus {
  return typeof status === 'string' && RUN_STATUS_VALUES.includes(status as RunStatus);
}

export const RunStatusTag: React.FC<RunStatusTagProps> = ({ status }) => {
  const resolved = resolveRunStatusForDisplay(status);
  const config = STATUS_CONFIG[resolved];

  if (!config) {
    return null;
  }

  const Icon = config.icon;

  return (
    <div className={`${styles.tag} ${config.colorClass}`}>
      <Icon className={styles.icon} />
      <span className={styles.label}>{config.label}</span>
    </div>
  );
};
